import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography
} from '@mui/material'
import { format, isValid as isValidDate, parseJSON } from 'date-fns'

import DeliveryAPI from '../../../../../../api/orbitV1/DeliveryAPI'
import OrderAPI from '../../../../../../api/orbitV1/OrderAPI'
import BreadCrumbPaths from '../../../../../../common/ui/breadCrumbs'
import {
  ColorBoxContent,
  ColorBoxHeader
} from '../../../../../../common/ui/colorBox'
import {
  OrderChangeState,
  OrderChangeStateActions
} from '../../../../../../types/order.type'
import SelectedOrderHeader from '../../components/SelectedOrderHeader'
import RecommendedActionsDialogBox from '../../ViewIndividualOrder/components/RecommendedActionsDialogBox'

import DeliveryLoadingDialog from './DeliveryLoadingDialog'

function HandleOrderDeliveryPage() {
  const { orderId } = useParams()
  const navigate = useNavigate()

  /**
   * Fetch from backend and store into state.
   * @type {[import('../../../../../../types/order.type').OrderModel | null, *]}
   */
  const [order, setOrder] = useState(null)
  const [delivery, setDelivery] = useState(null)
  const [value, setValue] = useState('manual')
  const [dialogIsOpen, setDialogIsOpen] = useState(false)
  const [deliveryQuote, setDeliveryQuote] = useState(null)
  const [reqBody, setReqBody] = useState({
    action: null
  })
  const [deliveryLoading, setDeliveryLoading] = useState(false)
  const [deliveryOkay, setDeliveryOkay] = useState(false)

  const handleChange = (event) => {
    setValue(event.target.value)
  }

  useEffect(() => {
    async function getOrder() {
      try {
        const orderStatus = await OrderAPI.getOrderStatus(orderId)
        if (orderStatus.data) setOrder(orderStatus.data)
      } catch (error) {
        toast.error(error.message)
      }
    }

    async function getDelivery() {
      try {
        const deliveryStatus = await DeliveryAPI.getDelivery(orderId)
        if (deliveryStatus.data) setDelivery(deliveryStatus.data)
      } catch (error) {
        toast.error(error.message)
      }
    }

    getOrder()
    getDelivery()
  }, [orderId])

  useEffect(() => {
    if (value === 'grab') {
      getDeliveryQuote()
    }
  }, [value])

  function formatDateField(dateString) {
    const date = parseJSON(dateString)
    if (!isValidDate(date)) {
      return '---'
    }
    return format(date, 'dd-MM-yyyy HH:mm')
  }

  const getDeliveryQuote = async () => {
    try {
      const grabDeliveryQuote = await DeliveryAPI.getGrabDeliveryQuote(orderId)
      setDeliveryQuote(grabDeliveryQuote.data)
    } catch (error) {
      toast.error(
        'Error fetching quotations for delivery. Please contact your admin for support.'
      )
    }
  }

  // TODO: Write created delivery object into mongo
  const createDelReq = async () => {
    try {
      await DeliveryAPI.createGrabDelivery(orderId)
    } catch (error) {
      toast.error(
        'Error creating a delivery for your order. Contact your admin, or use the manual delivery.'
      )
    }
  }

  if (!order || !delivery) {
    return <div>Loading</div>
  }

  if (order.status !== OrderChangeState.SCHEDULED_FOR_DELIVERY) {
    return <div>Cannot manage delivery for Order {orderId}</div>
  }

  const PageBreadCrumbs = () => (
    <BreadCrumbPaths
      paths={[
        {
          route: '/hq/manage-orders',
          name: 'Manage Orders'
        },
        {
          route: `/hq/manage-orders/${order.orderNo}`,
          name: `Manage ${order.orderNo}`
        },
        {
          route: `/hq/manage-orders/${order.orderNo}/handle-order-delivery`,
          name: 'Handle Order Delivery'
        }
      ]}
    />
  )

  const DeliveryRadio = () => (
    <RadioGroup
      aria-labelledby='demo-controlled-radio-buttons-group'
      name='controlled-radio-buttons-group'
      value={value}
      onChange={handleChange}
      row
      defaultValue='manual'
    >
      <FormControlLabel value='manual' control={<Radio />} label='Manual' />
      <FormControlLabel value='grab' control={<Radio />} label='Grab' />
    </RadioGroup>
  )

  const DeliveryInfo = () => (
    <table>
      <tbody>
        <tr>
          <td style={{ paddingRight: '3rem' }}>
            <strong>Contact Name:</strong>
          </td>
          <td>{delivery.contactName}</td>
        </tr>
        <tr>
          <td style={{ paddingRight: '3rem' }}>
            <strong>Contact Number:</strong>
          </td>
          <td>+65 {delivery.contactNumber}</td>
        </tr>
        <tr>
          <td style={{ paddingRight: '3rem' }}>
            <strong>Delivery Address:</strong>
          </td>
          <td>{delivery.address + ', ' + delivery.postalCode}</td>
        </tr>
        <tr>
          <td style={{ paddingRight: '3rem' }}>
            <strong>Preferred Delivery Slot:</strong>
          </td>
          <td>
            {delivery.deliveryDate +
              ', ' +
              delivery.deliveryStartTime +
              ' - ' +
              delivery.deliveryEndTime}
          </td>
        </tr>
      </tbody>
    </table>
  )

  const GrabQuotation = () => {
    if (!deliveryQuote) {
      return <div> Loading... </div>
    } else {
      return (
        <>
          <table>
            <tbody>
              <tr>
                <td style={{ paddingRight: '3rem' }}>
                  <strong>Est. Pickup Time:</strong>
                </td>
                <td>
                  {formatDateField(deliveryQuote[0].estimatedTimeline.pickup)}
                </td>
              </tr>
              <tr>
                <td style={{ paddingRight: '3rem' }}>
                  <strong>Est. Delivery Time:</strong>
                </td>
                <td>
                  {formatDateField(deliveryQuote[0].estimatedTimeline.dropoff)}
                </td>
              </tr>
              <tr>
                <td style={{ paddingRight: '3rem' }}>
                  <strong>Cost:</strong>
                </td>
                <td>S${deliveryQuote[0].amount}</td>
              </tr>
            </tbody>
          </table>

          <Button
            variant='filled'
            width='100%'
            onClick={() => {
              if (value === 'manual') {
                setReqBody({
                  action: OrderChangeStateActions.DELIVERY_START
                })
              } else if (value === 'grab') {
                createDelReq()
              }
              setDialogIsOpen(true)
            }}
          >
            Book A Grab Delivery
          </Button>
        </>
      )
    }
  }

  return (
    <>
      <ColorBoxHeader>Handle Order Delivery</ColorBoxHeader>
      <PageBreadCrumbs />
      <ColorBoxContent style={{ marginTop: '1rem' }}>
        <SelectedOrderHeader order={order} />
      </ColorBoxContent>

      <ColorBoxContent style={{ marginTop: '1rem' }}>
        <Box sx={{ marginTop: '12px' }}>
          <DeliveryInfo />
          <DeliveryRadio />
        </Box>
        <Divider sx={{ width: '100%' }} />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            textAlign: 'center',
            marginBottom: 4
          }}
        >
          {value === 'manual' ? (
            <Button
              variant='filled'
              onClick={() => {
                setDialogIsOpen(true)
                setReqBody({
                  action: OrderChangeStateActions.DELIVERY_START
                })
              }}
            >
              Confirm Delivery Pickup
            </Button>
          ) : (
            <>
              <Typography variant='h6'>
                <GrabQuotation />
              </Typography>
            </>
          )}
        </Box>
      </ColorBoxContent>

      <RecommendedActionsDialogBox
        isOpen={dialogIsOpen}
        orderNo={order.orderNo}
        onClose={() => {
          setDialogIsOpen(false)
          if (value === 'manual') {
            navigate(`/hq/manage-orders/${orderId}`)
          }
        }}
        updateStateReqBody={reqBody}
        deliveryType={value}
        setDeliveryLoading={setDeliveryLoading}
      />

      <DeliveryLoadingDialog
        order={order}
        orderNo={order.orderNo}
        isOpen={deliveryLoading}
        onClose={() => {
          setDeliveryLoading(false)
          navigate(`/hq/manage-orders/${order.orderNo}`)
        }}
        deliveryOkay={deliveryOkay}
        setDeliveryOkay={setDeliveryOkay}
      />
    </>
  )
}

export default HandleOrderDeliveryPage
